import { useState, useEffect, useCallback, useMemo } from "react";
import { DynamicDatePicker } from "../../../components/DatePicker";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  getGridcoIntraGen,
  getGridcoIsgs,
  getGridcoRegen,
  getOverviewGenMetrics,
} from "../../../Rest_api/RestApi";
import { formatDateForAPI, R_CHART_TYPES } from "../const";
import { getMonthFromDate } from "../../../extensions/date";
import { GenerationCommon } from "../../../models/gridco";
import { BrushStart } from "../../../models/chart_model";
import { SearchBox } from "../../../components/Search";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { titleCase } from "../../../extensions/extension";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { renderHourTick } from "../../../components/recharts/components";
import { useSelector } from "react-redux";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { stringToConsistentColor } from "../../../extensions/string";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { GridcoOverviewGenrationData } from "../../../models/metrics";
import { MetricChipHead } from "../../../components/Metrics";

const CHART_TYPES = [R_CHART_TYPES[0], R_CHART_TYPES[1], R_CHART_TYPES[2]];
const GROUP_BY = [
  { lable: "Area", value: "area" },
  { lable: "Ownership", value: "ownership" },
  { lable: "Generator Type", value: "generator_type" },
  { lable: "Station", value: "station_name" },
];
const UNITS = ["MW", "MWh", "MU", "LU"];

const INTERVAL = [
  {
    label: "Time Slot",
    key: "time",
    name: "Time Slot",
    firstKey: "slot",
    secondKey: "dateIndex",
    values: ["slot", "dateIndex"],
    isTime: true,
  },
  {
    label: "Date",
    key: "dateIndex",
    name: "Date",
    firstKey: "dateIndex",
    secondKey: undefined,
    values: ["dateIndex"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    name: "Month",
    firstKey: "month",
    secondKey: undefined,
    values: ["month"],
    isTime: false,
  },
  {
    label: "Generator Type",
    key: "generator_type",
    name: "Generator Type",
    firstKey: "generator_type",
    secondKey: undefined,
    values: ["generator_type"],
    isTime: false,
  },
  {
    label: "Station",
    key: "station_name",
    name: "Station",
    firstKey: "station_name",
    secondKey: undefined,
    values: ["station_name"],
    isTime: false,
  },
];

export function GridcoTrend() {
  const [TableData, setTableData] = useState<any[]>([]);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [data, setData] = useState<GenerationCommon[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [uniqueSource, setUniqueSource] = useState<string[]>([]);
  const [uniqueStations, setUniqueStations] = useState<string[]>([]);
  const [selectedStation, setSelectedStation] = useState<any[]>([]);
  const [selectedSource, setSelectedSource] = useState<string[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(CHART_TYPES[2]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedXaxis, setSelectedXaxis] = useState(INTERVAL[0]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[0].value);
  const [loading, setLoading] = useState(true);
 const [metrics, setMetrics] = useState<GridcoOverviewGenrationData | null>(
   null,
 );
    const [metricsLoading, setMetricsLoading] = useState(false);
  const fetchMetrics = useCallback(async () => {
      if (!DefaultDates[activeTab]?.defaultTrendDates) {
        setMetrics(null);
        setMetricsLoading(false);
        return;
      }
      setMetricsLoading(true);
  
      try {
        const originalDates = DefaultDates[activeTab].defaultTrendDates;
  
        const ranges = originalDates
          .filter((date: any) => {
            // Skip forecast entries — value has 3 parts: "start,end,Model 1"
            const commaParts = date.value.split(",");
            return commaParts.length <= 2;
          })
          .map((date: any) => {
            let start: string;
            let end: string;
  
            if (date.value.includes(" to ")) {
              [start, end] = date.value
                .split(" to ")
                .map((s: string) => s.trim());
            } else {
              const parts = date.value.split(",").map((s: string) => s.trim());
              start = parts[0];
              end = parts[1] ?? parts[0];
            }
  
            return {
              startDate: formatDateForAPI(new Date(start)),
              endDate: formatDateForAPI(new Date(end)),
              blocks: selectedBlock.map((block: any) => block.value),
            };
          })
          .filter((range: any) => range.startDate && range.endDate);
        if (ranges.length === 0) {
          setMetrics(null);
          setMetricsLoading(false);
          return;
        }
        const metricsData = await getOverviewGenMetrics({
          ranges,
          aggregated: true,
         
        });
  
        setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
      } catch (error) {
        console.error("Error fetching metrics:", error);
        setMetrics(null);
      } finally {
        setMetricsLoading(false);
      }
    }, [DefaultDates, activeTab, selectedBlock]);
    useEffect(() => {
      fetchMetrics();
    }, [fetchMetrics]);
  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultTrendDates === undefined) {
      return;
    }
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultTrendDates;

    const promises = dates.map(async (date: any) => {
      const [startDate, endDate] = date.value.split(",");

      const [RegenData, IntraData, IsgsData] = await Promise.all([
        getGridcoRegen({ startDate, endDate }),
        getGridcoIntraGen({ startDate, endDate }),
        getGridcoIsgs({ startDate, endDate }),
      ]);

      return [
        ...IntraData.map((item: any) => ({
          ...item,
          dates: date.label,
          value_mw: item.experi_value,
          value_mwh: item.experi_value / 4,
          area: "Intrastate",
        })),
        ...IsgsData.map((item: any) => ({
          ...item,
          dates: date.label,
          value_mw: item.experi_value,
          value_mwh: item.experi_value / 4,
          area: "Interstate",
        })),
        ...RegenData.map((item: any) => ({
          ...item,
          dates: date.label,
          area: "Intrastate",
        })),
      ];
    });

    const results = await Promise.all(promises);
    const newData: GenerationCommon[] = results.flat();

    setData(newData);
    setUniqueStations(
      Array.from(
        new Set(
          newData
            .map(
              (item) =>
                item.station_name || item.sub_station || item.beneficiery_from
            )
            .filter((station) => station !== undefined && station !== null)
        )
      )
    );

    setUniqueSource(
      Array.from(new Set(newData.map((item) => titleCase(item.generator_type))))
    );
    setUniqueBlock(Array.from(new Set(newData.map((item) => item.time_block))));
    setLoading(false);
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    const processedData = data.map((item) => ({
      ...item,
      value_mw: item.value_mw || 0,
      value_mwh: item.value_mw / 4,
      value_mu: item.value_mw / 4000,
      value_lu: item.value_mw / 400,
      month: getMonthFromDate(item.date),
      slot: item.time_block,
      generator_type:
        item.generator_type.charAt(0).toUpperCase() +
        item.generator_type.slice(1),
      station_name:
        item.station_name || item.sub_station || item.beneficiery_from,
    }));

    let filteredData = processedData;

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.slot)
      );
    }

    if (selectedSource.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedSource.includes(titleCase(item.generator_type))
      );
    }

    if (selectedStation.length > 0) {
      filteredData = filteredData.filter((item) => {
        return selectedStation.map((i) => i.label).includes(item.station_name);
      });
    }

    function getKeys() {
      if (selectedXaxis.key === "time") {
        return ["dateIndex", "slot"];
      } else if (selectedXaxis.key === "date") {
        return ["dateIndex"];
      } else {
        return [selectedXaxis.values[0]];
      }
    }

    const par: ChartDataParams = {
      keys: getKeys(),
      tableKeys: ["dateIndex", "slot"],
      groupBy: ["dates"].concat(selectedGroupKeys.toLowerCase()),
      volumeKey: "",
      aggregationKeys: ["value_mwh", "value_mw", "value_mu", "value_lu"],
      averageKeys: ["value_mw"],
      averageDuplicationKeys: ["dateIndex", "slot"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);

    // Calculate total values when groupBy is "area"
    let processedChartData = cData.data;

    if (selectedGroupKeys === "area") {
      processedChartData = cData.data.map((item: any) => {
        const result = { ...item };

        // Calculate totals for each date label
        cData.keys.forEach((key: string) => {
          const datePrefix = key.slice(0, 6); // Extract date prefix (e.g., "JAN-24", "FEB-22")

          const interstateValueMW =
            item[`${datePrefix}_Interstate_value_mw`] || 0;
          const intrastateValueMW =
            item[`${datePrefix}_Intrastate_value_mw`] || 0;
          const interstateValueMWh =
            item[`${datePrefix}_Interstate_value_mwh`] || 0;
          const intrastateValueMWh =
            item[`${datePrefix}_Intrastate_value_mwh`] || 0;
          const interstateValueMU =
            item[`${datePrefix}_Interstate_value_mu`] || 0;
          const intrastateValueMU =
            item[`${datePrefix}_Intrastate_value_mu`] || 0;

          result[`${datePrefix}_total_value_mw`] =
            interstateValueMW + intrastateValueMW;
          result[`${datePrefix}_total_value_mwh`] =
            interstateValueMWh + intrastateValueMWh;
          result[`${datePrefix}_total_value_mu`] =
            interstateValueMU + intrastateValueMU;
        });

        return result;
      });
    }

    let processedTableData = cData.tableData;
    if (selectedGroupKeys === "area") {
      processedTableData = cData.tableData?.map((item: any) => {
        const result = { ...item };

        // Calculate totals for each date label in table data
        cData.keys.forEach((key: string) => {
          const datePrefix = key.slice(0, 6);

          const interstateValueMW =
            item[`${datePrefix}_Interstate_value_mw`] || 0;
          const intrastateValueMW =
            item[`${datePrefix}_Intrastate_value_mw`] || 0;
          const interstateValueMWh =
            item[`${datePrefix}_Interstate_value_mwh`] || 0;
          const intrastateValueMWh =
            item[`${datePrefix}_Intrastate_value_mwh`] || 0;
          const interstateValueMU =
            item[`${datePrefix}_Interstate_value_mu`] || 0;
          const intrastateValueMU =
            item[`${datePrefix}_Intrastate_value_mu`] || 0;
          const interstateValueLU =
            item[`${datePrefix}_Interstate_value_lu`] || 0;
          const intrastateValueLU =
            item[`${datePrefix}_Intrastate_value_lu`] || 0;

          result[`${datePrefix}_total_value_mw`] =
            interstateValueMW + intrastateValueMW;
          result[`${datePrefix}_total_value_mwh`] =
            interstateValueMWh + intrastateValueMWh;
          result[`${datePrefix}_total_value_mu`] =
            interstateValueMU + intrastateValueMU;
          result[`${datePrefix}_total_value_lu`] =
            interstateValueLU + intrastateValueLU;
        });

        return result;
      });
    }

    setTableData(processedTableData || []);
    setChartData(processedChartData);
    setChartKeys(cData.keys);
  }, [
    data,
    selectedBlock,
    selectedGroupKeys,
    selectedSource,
    selectedStation,
    selectedXaxis,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  function _handleGroupByChange(index: number) {
    setSelectedGroupKeys(GROUP_BY[index].value);
  }

  const handleKeyChange = (index: number, setSelectedUnit: any) => {
    // if (INTERVAL[index].firstKey != "slot") {
    //   setSelectedUnit(UNITS[1]);
    // }
    // setSelectedUnit("MW");
    setSelectedXaxis(INTERVAL[index]);
  };

  const handlexDataKey = useCallback(() => {
    if (selectedXaxis.key === "time") {
      if (selectedBlock.length > 0) {
        return "slot";
      }
      return "slot";
    } else {
      // if (selectedUnit !== DEMAND_UNITS[1]) {
      //   setSelectedUnit(DEMAND_UNITS[1]);
      // }
      return selectedXaxis.key;
    }
  }, [selectedBlock.length, selectedXaxis.key]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  const summaryExtraKeys = selectedXaxis.isTime
    ? [
        "slot", // Changed dateIndex to date
        selectedXaxis.values[1],
      ]
    : [selectedXaxis.values[0]];

  const summaryExtraLabels = selectedXaxis.values.map(
    (value) => (value === "date" ? "Date" : value) // Simplified label mapping
  );

  const legends = useMemo(() => {
    const baseLegends = chartKeys.map((item, index) => {
      return {
        dataKey: item + "_value_" + selectedUnit.toLowerCase(),
        name: item,
        legendColor: undefined,
        // stroke: stringToConsistentColor(item),
        type: selectedChartType.value,
        stackId: selectedChartType.stackId,
      };
    });

    // Add Total legends for each date when groupBy is "area"
    if (selectedGroupKeys === "area") {
      const totalLegends = chartKeys.map((key: string) => {
        const datePrefix = key.slice(0, 6);
        return {
          dataKey: `${datePrefix}_total_value_${selectedUnit.toLowerCase()}`,
          name: `${datePrefix}_Total`,
          legendColor: undefined,
          // stroke: stringToConsistentColor(`${datePrefix}_Total`),
          type: selectedChartType.value,
          stackId: selectedChartType.stackId,
        };
      });

      // Get unique total legends (in case there are duplicate date prefixes)
      const uniqueTotalLegends = totalLegends.filter(
        (legend, index, self) =>
          index === self.findIndex((l) => l.dataKey === legend.dataKey)
      );

      return [...baseLegends, ...uniqueTotalLegends];
    }

    return baseLegends;
  }, [chartKeys, selectedUnit, selectedChartType, selectedGroupKeys]);

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Generation Trend Analysis",
      dates: DefaultDates[activeTab]?.defaultTrendDates,
    }),
  };

  function secondDataKey() {
    if (selectedXaxis.isTime) {
      if (selectedBlock.length > 0) {
        return "dateIndex";
      }
      return "dateIndex";
    } else {
      return undefined;
    }
  }

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X Axis"
            items={INTERVAL.map((item) => item.name)}
            selectedItems={[selectedXaxis.name]}
            onSelect={(item, index) => {
              handleKeyChange(index, setSelectedUnit);
            }}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              setSelectedUnit(item);
            }}
          />

          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((item) => item.lable)}
            selectedItems={[
              GROUP_BY.find((g) => g.value === selectedGroupKeys)?.lable || "",
            ]}
            onSelect={(item, index) => {
              _handleGroupByChange(index);
            }}
          />

          <FilterMenu
            title="Generator Type"
            items={uniqueSource}
            selectedItems={selectedSource}
            setSelectedItems={setSelectedSource}
          />

          <FilterMenu
            title="Chart Type"
            items={CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              title="Thermal Generation"
              items={[
                {
                  label: "Total",
                  value:
                    metrics.thermal_generation["Total Thermal Generation (MU)"]
                      .value,
                  unit: metrics.thermal_generation[
                    "Total Thermal Generation (MU)"
                  ].unit,
                },
                {
                  label: "Peak",
                  value:
                    metrics.thermal_generation["Peak Thermal Generation (MW)"]
                      .value,
                  unit: metrics.thermal_generation[
                    "Peak Thermal Generation (MW)"
                  ].unit,
                },
              ]}
            />
            <MetricChipHead
              title="RE Generation"
              items={[
                {
                  label: "Total",
                  value:
                    metrics.re_generation["Total RE Generation (MU)"].value,
                  unit: metrics.re_generation["Total RE Generation (MU)"].unit,
                },
                {
                  label: "Peak",
                  value: metrics.re_generation["Peak RE Generation (MW)"].value,
                  unit: metrics.re_generation["Peak RE Generation (MW)"].unit,
                },
              ]}
            />
            <MetricChipHead
              title="Percentage Share"
              items={[
                {
                  label: "Thermal",
                  value: metrics.percentage_share["Thermal % Share"].value,
                  unit: metrics.percentage_share["Thermal % Share"].unit,
                },
                {
                  label: "RE",
                  value: metrics.percentage_share["RE % Share"].value,
                  unit: metrics.percentage_share["RE % Share"].unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Generation Trend"
              subtitle="Fetching Gridco generation trend analysis data…"
            />
          ) : (
            <>
              <div className="gas-heading">
                <h2 className="chartHeading w-80">Generation Trend Analysis</h2>
                <div className="flex gap-x-2 w-full justify-end">
                  <SearchBox
                    options={uniqueStations.map((item) => {
                      return { value: item, label: item };
                    })}
                    isMany={true}
                    placeholder="Search Station"
                    selectedOptions={selectedStation}
                    onChange={setSelectedStation}
                  />
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      dataTab.label === "Chart" &&
                        downloadChartTableCSV(tablePar);
                      //

                      dataTab.label === "Summary" &&
                        downloadSummaryCSV(tablePar);

                      dataTab.label === "Table" &&
                        downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 96 * 7 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={selectedXaxis.isTime}
                  onlyTitle={true}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends}
                  defaultSelectedLegends={selectedLegends}
                  onLegendSelect={handleLegendSelect}
                  xDataKey={xDataKey}
                  secondXDataKey={secondDataKey()}
                  xTick={
                    selectedXaxis.isTime && selectedBlock.length === 0
                      ? (value: any, index: any) => {
                          return renderHourTick(value, 10);
                        }
                      : undefined
                  }
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={
                <p>
                  1. SLDC: Daily CGP scheduling <br /> 2.ERLDC: Daily final
                  scheduling <br /> 3. ABT Meter readings (W exp) :
                  Solar/SHEP/Biomass. <br /> 4. Gridco : Vedanta IPP, OPGC Stage
                  II, State Hydro.
                </p>
              }
              text="1. All scheduling to Odisha Solar beneficiary is considered hybrid power.
              2. OPGC Stage I taken from state hydro file (IB Unit I & II).
              3. Solar/SHEP/Biomass has been named mapped.
              4. OPGC Stage II Entitlement is taken from Declared Capacity.
              5. Hydro Entitlement is same as Expp.
              6. Vedanta Entitlement is taken from Total Injection (considering backdown).
              7. Wind power generation is taken from ERLDC (GNA & LTA).
              
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
